'use client';

import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import KlaviyoOauth from '@/app/template/frontend/forms/KlaviyoOauth';
import { Button } from '@/components/ui/button';
import { useOnboardingStore } from '../../store';
import { StepWrapper } from '../shared/StepWrapper';

interface ConnectStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function ConnectStep({ onNext, onBack }: ConnectStepProps) {
  const { companyId, klaviyoConnected, setKlaviyoConnected } = useOnboardingStore();
  const searchParams = useSearchParams();

  // Detect successful OAuth return via ?klaviyo=connected query param
  useEffect(() => {
    if (searchParams.get('klaviyo') === 'connected') {
      setKlaviyoConnected(true);
    }
  }, [searchParams, setKlaviyoConnected]);

  // Already connected — show success and continue button
  if (klaviyoConnected) {
    return (
      <StepWrapper
        title="Klaviyo connected!"
        subtitle="Your responses will sync directly to subscriber profiles."
      >
        <div className="space-y-5">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex flex-col items-center gap-3 py-6"
          >
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
          </motion.div>

          <Button
            onClick={onNext}
            className="w-full bg-primary hover:bg-primary/90 rounded-[13px] h-12 text-base"
          >
            Continue
          </Button>
        </div>
      </StepWrapper>
    );
  }

  return (
    <StepWrapper
      title="Connect Klaviyo"
      subtitle="Load your email templates and sync form responses straight into subscriber profiles."
    >
      <div className="space-y-5">
        {companyId ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-5 rounded-[13px] border bg-white"
          >
            <KlaviyoOauth
              companyId={companyId}
              hideButton={false}
              returnUrl="/onboarding"
            />
          </motion.div>
        ) : (
          <div className="p-4 rounded-[13px] border border-amber-200 bg-amber-50 text-amber-800 text-sm">
            <p>You can connect Klaviyo later from your dashboard settings.</p>
          </div>
        )}

        <button
          onClick={onNext}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors text-center w-full"
        >
          Skip, I'll do this later
        </button>

        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
      </div>
    </StepWrapper>
  );
}
